import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MoonStarIcon, SparklesIcon } from 'lucide-react';
import { SearchField } from '../components/SearchField';
import { SectionHeading } from '../components/SectionHeading';
import { NumberPill } from '../components/NumberPill';
import { NumberCard } from '../components/NumberCard';
import { getNumber, popularNumberIds, recommendedNumberIds, trendingNumberIds } from '../data/numbers';
import { resolveExact } from '../utils/search';
const ease = [0.23, 1, 0.32, 1] as const;
export function Home() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const submit = () => {
    const q = query.trim();
    if (!q) {
      navigate('/search');
      return;
    }
    const exact = resolveExact(q);
    navigate(exact ? `/number/${exact.id}` : `/search?q=${encodeURIComponent(q)}`);
  };
  const recommended = recommendedNumberIds.map(id => getNumber(id));
  const trending = trendingNumberIds.slice(0, 4).map(id => getNumber(id));
  const portal = getNumber('1111');
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.26,
    ease
  }} data-id="element-112" data-mp="2vy82sexq2jmn">
      <header data-id="element-113" data-mp="2mx0i4zo4phb1">
        <div className="flex items-center gap-2" data-id="element-114" data-mp="2mxjnlgwled86">
          <MoonStarIcon className="h-3.5 w-3.5 text-gold" strokeWidth={2} aria-hidden="true" data-id="element-115" data-mp="2mwqq76jix02p" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold" data-id="element-116" data-mp="2hzi8f72aey1z">
            Angel Numbers
          </p>
        </div>
        <h1 className="mt-5 text-[32px] font-light leading-[1.12] tracking-tight text-white" data-id="element-117" data-mp="287ukhg4xvz59">
          What is the universe
          <br data-id="element-118" data-mp="239ssg5pjcphn" />
          <span className="font-normal" data-id="element-119" data-mp="23ac6hbc2etqe">trying to tell you?</span>
        </h1>
        <p className="mt-3.5 max-w-[300px] text-[14.5px] leading-relaxed text-lav-dim" data-id="element-120" data-mp="1z7pdh73nr2rm">
          Discover the meaning behind the numbers you keep seeing.
        </p>
      </header>

      <div className="mt-6" data-id="element-121" data-mp="1kkyzb54u5fom">
        <SearchField value={query} onChange={setQuery} onSubmit={submit} placeholder="Enter a number…" data-id="element-122" data-mp="1kjo07imc4w3u" />
      </div>

      <section className="mt-9" aria-labelledby="popular-heading" data-id="element-123" data-mp="1onvnrch2460h">
        <div className="mb-3 flex items-baseline justify-between gap-3" data-id="element-124" data-mp="1anck0hjmjm44">
          <h2 id="popular-heading" className="text-[15px] font-semibold tracking-tight text-white" data-id="element-125" data-mp="1fle14wxhpbcy">
            Popular numbers
          </h2>
          <Link to="/explore" className="rounded-full text-[12px] font-medium text-aura-bright transition-colors duration-200 ease-smooth hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-126" data-mp="23cjen5fyjrk3">
            See all
          </Link>
        </div>
        <div className="grid grid-cols-4 gap-2.5" data-id="element-127" data-mp="1yinm4iigqwwb">
          {popularNumberIds.map(id => <NumberPill key={id} id={id} label={getNumber(id)?.displayNumber ?? id} data-id="element-128" data-mp="2m8huamru7xa8" />)}
        </div>
      </section>

      <section className="mt-9" aria-labelledby="explore-heading" data-id="element-129" data-mp="2r2m7vovlz3ka">
        <h2 id="explore-heading" className="mb-3 text-[15px] font-semibold tracking-tight text-white" data-id="element-130" data-mp="fk98vhy3atm2">
          Start exploring
        </h2>
        <div className="flex flex-col gap-2.5" data-id="element-131" data-mp="oe4awh3x03xh">
          {recommended.map((entry, index) => entry && <motion.div key={entry.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.26,
          ease,
          delay: 0.05 + index * 0.05
        }} data-id="element-132" data-mp="kdlk8qdrd3wv">
                  <NumberCard entry={entry} variant="feature" data-id="element-133" data-mp="5near05qav8b" />
                </motion.div>)}
        </div>
      </section>

      {portal && <section className="mt-9" data-id="element-134" data-mp="n8h5efweynl">
          <SectionHeading title="Go deeper" action={{
        label: 'Library',
        to: '/explore'
      }} data-id="element-135" data-mp="1h2jgkd15p9w5" />
          <Link to={`/number/${portal.id}`} className="glass relative flex items-center gap-4 overflow-hidden rounded-3xl px-5 py-5 transition-[border-color,box-shadow] duration-200 ease-smooth hover:border-aura/45 hover:shadow-[0_0_34px_-14px_rgba(139,92,246,0.9)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-136" data-mp="1c3q8x18jwd3w">
            <span aria-hidden="true" className="absolute -right-12 -top-16 h-40 w-40 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.28) 0%, rgba(5,6,15,0) 70%)'
        }} data-id="element-137" data-mp="2he5mghwftqkn" />
            <span className="relative" data-id="element-138" data-mp="2vb2dajck381j">
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-139" data-mp="2v9r609jwji5o">
                <SparklesIcon className="h-3 w-3" strokeWidth={2} aria-hidden="true" data-id="element-140" data-mp="351ng6iz9iyq9" />
                Portal number
              </span>
              <span className="number-glow mt-2 block text-[30px] font-light leading-none tabular-nums text-white" data-id="element-141" data-mp="3a1rohlq0on5e">
                {portal.displayNumber}
              </span>
              <span className="mt-2 block text-[13px] leading-snug text-lav-dim" data-id="element-142" data-mp="12eq5yenn6dqr">
                {portal.shortMeaning}
              </span>
            </span>
          </Link>
        </section>}

      <section className="mt-9" data-id="element-143" data-mp="tds2bxh4888p">
        <h2 className="mb-3 text-[15px] font-semibold tracking-tight text-white" data-id="element-144" data-mp="tf37o1q7lo1x">Trending now</h2>
        <div className="grid grid-cols-2 gap-2.5" data-id="element-145" data-mp="ogif8oh788o1">
          {trending.map(entry => entry && <NumberCard key={entry.id} entry={entry} variant="tile" data-id="element-146" data-mp="1m1xnglc2zz92" />)}
        </div>
      </section>

      <p className="mt-8 text-[11.5px] leading-relaxed text-lav-dim/80" data-id="element-147" data-mp="1h4xhsbj58yih">
        Angel numbers are shared here as symbolic and reflective interpretations — never predictions or
        guarantees.
      </p>
    </motion.div>;
}